import cx from 'classnames';
import Image, { ImageProps } from 'next/image';
import { HTMLAttributeAnchorTarget } from 'react';

import { IntrinsicElement } from '../../../../shared/utilities/types/IntrinsicElement';
import { RATIO, Ratio } from '../../ratio/Ratio';
import { ReviewRating, ReviewRatingProps } from '../../review/rating/ReviewRating';
import { Typography } from '../../typography/Typography';

import style from './PointOfInterestCard.module.css';

export type Props = {
    image?: ImageProps;
    obfuscate?: boolean;
    path: string;
    placeName: string;
    placeNameElement?: IntrinsicElement;
    reviewRatingProps: ReviewRatingProps | null;
    description?: string;
    withDescription?: boolean;
    target?: HTMLAttributeAnchorTarget;
    onClick?: () => void;
};

export function PointOfInterestCard({
    image,
    obfuscate,
    placeName,
    placeNameElement = 'span',
    path,
    reviewRatingProps,
    description = '',
    withDescription = false,
    target,
    onClick,
}: Props) {
    const PlaceNameLinkHtmlTag = obfuscate ? 'div' : 'a';
    const placeNameAnchorProps = obfuscate ? {} : { href: path, target };

    const handleClick = () => {
        if (onClick) {
            onClick();
        }

        if (obfuscate) {
            if (target === '_self' || target === undefined) {
                window.location.assign(path);
            } else {
                window.open(path, target);
            }
        }
    };

    return (
        <div
            className={cx(style.card, {
                [style.withDescription]: withDescription,
            })}
        >
            <Ratio
                className={cx('imageOverlay', style.ratio)}
                ratio={PointOfInterestCard.PICTURE_RATIO}
            >
                {image ? (
                    <Image
                        {...image}
                        alt={image.alt}
                        fill
                        className={cx(image.className, style.image)}
                    />
                ) : null}
                <div className={style.info}>
                    <PlaceNameLinkHtmlTag
                        {...placeNameAnchorProps}
                        onClick={handleClick}
                        className={style.nameLink}
                    >
                        <Typography
                            as={placeNameElement}
                            scale="primary-m-extra-bold"
                            className={style.name}
                        >
                            {placeName}
                        </Typography>
                    </PlaceNameLinkHtmlTag>
                    {reviewRatingProps ? (
                        <ReviewRating
                            {...reviewRatingProps}
                            inverse
                            showRatingNumber
                            size="small"
                        />
                    ) : null}
                </div>
            </Ratio>
            {withDescription ? (
                <div className={style.descriptionWrapper}>
                    <Typography
                        as="div"
                        scale="primary-xs-regular"
                        className={style.description}
                        dangerouslySetInnerHTML={{ __html: description }}
                    />
                </div>
            ) : null}
        </div>
    );
}

PointOfInterestCard.PICTURE_RATIO = RATIO['16/10'];
